/* Aurora updates variant IDs synchronously and replaces button children after a section fetch.
   Observe only this product form, keeping labels and order properties aligned with the current ID. */
(() => {
  if (customElements.get('lounge-preorder')) return;
  customElements.define('lounge-preorder', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.component = this.closest('product-form');
      this.form = this.closest('form');
      this.root = this.closest('product-info') || this.component;
      this.panel = this.querySelector('[data-lounge-preorder-panel]');
      this.panelTemplates = [...this.querySelectorAll('[data-lounge-preorder-panel-template]')];
      if (!this.form || !this.component || !this.panel || !this.panelTemplates.length) return;
      this.confirm = this.querySelector('[data-lounge-preorder-confirm]');
      this.agree = this.querySelector('[data-lounge-preorder-agree]');
      /* 印を付けた変種だけを通す。選び直したら、もう一度読んでもらう。 */
      this.agreedFor = null;
      try {
        this.states = JSON.parse(this.querySelector('[data-lounge-preorder-states]').textContent);
      } catch { return; }
      this.variantId = this.form.querySelector('[data-product-form-variant]')?.value;
      this.controller = new AbortController();
      this.root.addEventListener('change', event => {
        if (event.target.matches('[data-product-form-variant]') && this.form.contains(event.target)) {
          this.variantId = event.target.value;
        } else {
          const picker = event.target.closest('variant-picker');
          if (!picker || picker.closest('product-info') !== this.closest('product-info')) return;
          this.variantId = picker.variant?.id ?? null;
        }
        this.agreedFor = null;
        this.synchronize();
      }, { signal: this.controller.signal });
      // Run before Aurora collects FormData or disables the submit button.
      this.component.addEventListener('submit', event => {
        this.synchronize();
        const state = this.states[this.variantId];
        if (!state || !state.available || state.mode === 'blocked') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        /* 予約のときは、注意事項を読んで印を付けてもらってから進む。 */
        if (state.mode === 'preorder' && this.confirm && this.agreedFor !== this.variantId) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.ask();
        }
      }, { capture: true, signal: this.controller.signal });
      this.prepareConfirm();
      this.observer = new MutationObserver(() => this.synchronize());
      this.observer.observe(this.component, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      this.synchronize();
    }
    prepareConfirm() {
      if (!this.confirm || !this.agree) return;
      const go = this.confirm.querySelector('[data-lounge-preorder-go]');
      const cancel = this.confirm.querySelector('[data-lounge-preorder-cancel]');
      const signal = this.controller.signal;
      const problem = this.confirm.querySelector('[data-lounge-preorder-error]');
      this.agree.addEventListener('change', () => {
        if (go) go.setAttribute('aria-disabled', this.agree.checked ? 'false' : 'true');
        if (problem && this.agree.checked) problem.hidden = true;
      }, { signal });
      cancel?.addEventListener('click', () => this.confirm.close(), { signal });
      /* 枠の外を押しても閉じる。窓そのものが的になるのは、まわりの暗がりの部分。 */
      this.confirm.addEventListener('click', event => {
        if (event.target === this.confirm) this.confirm.close();
      }, { signal });
      go?.addEventListener('click', () => {
        if (!this.agree.checked) {
          /* 理由を出して、印のところへ連れていく。黙って何も起きないのがいちばん困る。 */
          if (problem) problem.hidden = false;
          this.agree.focus();
          return;
        }
        if (problem) problem.hidden = true;
        this.agreedFor = this.variantId;
        this.confirm.close();
        /* 出し直す。上の見張りは agreedFor を見て、こんどは通す。 */
        this.form.requestSubmit(this.form.querySelector('button[name="add"]') || undefined);
      }, { signal });
    }
    ask() {
      if (!this.confirm) return;
      /* 見出しは「作品名　選んだサイズ」。サイズごとに変わるので、開くたびに書き直す。 */
      if (!this.hasAttribute('data-heading-fixed')) {
        const title = this.confirm.querySelector('.lounge-preorder__title');
        const name = this.dataset.productName || '';
        /* 容量の後ろの括弧書きは落とす。いまのバリエーション名には
           「（在庫完売中につき予約販売: 2027年1月旬頃お届け予定分）」まで入っていて、
           **すぐ下の「発送予定」と同じことを、より曖昧に言っている。** */
        const size = (this.states[this.variantId]?.title || '').split(/[（(]/)[0].trim();
        if (title && name) {
          /* 作品名とサイズは、地の文に混ぜず一つずつ札にする。
             ベタ書きだと、どこまでが名前でどこからがサイズか読み取る手間がかかる。 */
          const chip = text => {
            const span = document.createElement('span');
            span.className = 'lounge-preorder__chip';
            span.textContent = text;
            return span;
          };
          const parts = [chip(name)];
          if (size) parts.push(chip(size));
          title.replaceChildren(...parts);
        }
      }
      // 確認の窓の「発送予定」は、選んだサイズの予定に書き直す
      const whenValue = this.confirm.querySelector('.lounge-preorder__when-value');
      const whenText = this.states[this.variantId]?.when;
      if (whenValue && whenText) whenValue.textContent = whenText;
      if (this.agree) this.agree.checked = false;
      const go = this.confirm.querySelector('[data-lounge-preorder-go]');
      if (go) go.setAttribute('aria-disabled', 'true');
      const problem = this.confirm.querySelector('[data-lounge-preorder-error]');
      if (problem) problem.hidden = true;
      if (this.confirm.open) return;
      if (typeof this.confirm.showModal === 'function') this.confirm.showModal();
      else this.confirm.setAttribute('open', '');
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
    }
    synchronize() {
      const state = this.states[this.variantId];
      const preorder = state?.mode === 'preorder' && state.available;
      // 発送予定はサイズごとに違うことがあるので、サイズごとの雛形に差し替える
      const key = preorder ? 'preorder:' + this.variantId : 'regular';
      if (this.panelKey !== key) {
        this.panelKey = key;
        const template = this.panelTemplates.find(t => t.dataset.variantId === String(this.variantId)) || this.panelTemplates[0];
        this.panel.replaceChildren(...(preorder ? [template.content.cloneNode(true)] : []));
      }
      this.panel.hidden = !preorder;
      const label = !state ? window.auroraThemeLocales?.variantStrings?.unavailable :
        !state.available || state.mode === 'blocked' ? this.dataset.soldOutLabel :
        preorder ? this.dataset.preorderLabel : this.dataset.defaultLabel;
      this.component.querySelectorAll('button[name="add"]').forEach(button => {
        const buttonLabel = button.dataset.mouetteLabel || label;
        let text = button.querySelector('[data-button-text]');
        // Aurora removes the text span when an option combination is unavailable.
        if (!text) {
          text = document.createElement('span');
          text.setAttribute('data-button-text', '');
          button.replaceChildren(text);
          const spinner = document.createElement('div');
          spinner.className = 'loading-spinner btn__loading-spinner';
          button.append(spinner);
        }
        if (buttonLabel && text.textContent.trim() !== buttonLabel) text.textContent = buttonLabel;
        // 予約のときは、ボタンの2行目にそのサイズの発送予定を書く（2026-10-17 本人）。日本語は「頃」のあとで折り返す
        let sub = button.querySelector('[data-lounge-preorder-sub]');
        const shipText = preorder && state.ship ? String(state.ship) : '';
        if (shipText) {
          if (!sub) {
            sub = document.createElement('span');
            sub.setAttribute('data-lounge-preorder-sub', '');
            sub.className = 'lounge-preorder__sub';
            text.after(sub);
          }
          if (sub.dataset.text !== shipText) {
            sub.dataset.text = shipText;
            const pieces = shipText.split(/(?<=頃)/);
            sub.replaceChildren(...pieces.flatMap((p, i) => i ? [document.createElement('wbr'), p] : [p]));
          }
          button.classList.add('lounge-preorder__has-sub');
        } else {
          sub?.remove();
          button.classList.remove('lounge-preorder__has-sub');
        }
        if (buttonLabel && button.getAttribute('aria-label') !== buttonLabel) button.setAttribute('aria-label', buttonLabel);
        if ((!state || !state.available || state.mode === 'blocked') && !button.disabled) button.disabled = true;
        // Aurora owns enabling and loading while its section request is in flight.
      });
      this.component.querySelectorAll('[data-lounge-preorder-checkout]').forEach(node => {
        node.hidden = Boolean(!state || !state.available || preorder || state.mode === 'blocked');
      });
      const pickup = this.root.querySelector('pickup-availability');
      if (pickup) pickup.hidden = Boolean(!state || !state.available || preorder || state.mode === 'blocked');
    }
  });
})();

/* 商品カードの EXCLUSIVE の行（2026-11-03 本人）。横に並んだ1列に EXCLUSIVE の商品が1つでもあれば、
   その列のカードに lpc-ex-row を付けて空の行も1行分あけ、商品名の高さをそろえる。無い列は詰めたまま。
   名前の2行目（読み・色・素材）も同じ考え方で、列に2行目のある商品があれば lpc-sub-row を付けて
   2行目の無い商品に空の行を出す（2026-11-04）。
   並びは画面の幅・絞り込み・売り切れの並び替えで変わるので、そのたびに数え直す。 */
(() => {
  let queued = false;
  // 写真の読み込みなどでカードの大きさや位置が変わったら数え直す
  const resized = new ResizeObserver(() => queue());
  const run = () => {
    queued = false;
    const rows = new Map();
    // 位置は offsetTop で測る（カードが現れるときの動き transform に左右されないように）
    const pageTop = (el) => { let y = 0; for (; el; el = el.offsetParent) y += el.offsetTop; return y; };
    document.querySelectorAll('.product-card').forEach((card) => {
      if (!card.querySelector('.lpc-ex, .lpc-sub-blank')) return;
      resized.observe(card);
      if (!card.offsetWidth) { card.classList.remove('lpc-ex-row', 'lpc-sub-row'); return; }
      const key = pageTop(card);
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key).push(card);
    });
    rows.forEach((cards) => {
      const has = cards.some((c) => c.querySelector('.lpc-ex:not(:empty)'));
      cards.forEach((c) => c.classList.toggle('lpc-ex-row', has));
      const sub = cards.some((c) => c.querySelector('.product-card__title-sub:not(.lpc-sub-blank)'));
      cards.forEach((c) => c.classList.toggle('lpc-sub-row', sub));
    });
  };
  const queue = () => { if (!queued) { queued = true; requestAnimationFrame(run); } };
  queue();
  window.addEventListener('load', queue);
  window.addEventListener('resize', queue);
  new MutationObserver((list) => {
    if (list.some((m) => m.addedNodes.length)) queue();
  }).observe(document.body, { childList: true, subtree: true });
})();
